/**
 * Generic two-button confirmation dialog. `A11Y-021`/`A11Y-044`: focus traps
 * inside it, and it names what will be lost rather than asking a bare
 * "are you sure?".
 *
 * Extracted from `LeaveConfirmDialog`, Sprint 5c stage 1, once `SHELL-081`
 * (restart) needed the identical trap -- Tab/Shift+Tab wrapping between
 * exactly two buttons, Escape answering the non-destructive way, focus
 * moving in on open and back to the screen heading on close. Three
 * consumers within one sprint (leave, restart, and this) is a reused
 * pattern earning generalisation now, not a second game's needs guessed at
 * in advance (CLAUDE.md rule 2 is about game logic, not shell-internal UI
 * that already has three real call sites).
 *
 * `cancel` is always the first, focused button and the non-destructive
 * answer -- `Escape` calls it too. `confirm` is second and does whatever the
 * caller's copy says it does; this component has no opinion on which one is
 * "worse", only that the safe one is reachable fastest.
 */

import { useLayoutEffect, useRef } from 'preact/hooks';

import { focusScreenHeading } from '@shell/screen-transition';

export interface ConfirmDialogProps {
  headingId: string;
  detailId: string;
  heading: string;
  detail: string;
  cancelLabel: string;
  confirmLabel: string;
  onCancel: () => void;
  onConfirm: () => void;
}

export function ConfirmDialog({
  headingId,
  detailId,
  heading,
  detail,
  cancelLabel,
  confirmLabel,
  onCancel,
  onConfirm,
}: ConfirmDialogProps) {
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  const confirmRef = useRef<HTMLButtonElement | null>(null);

  useLayoutEffect(() => {
    cancelRef.current?.focus();
    return () => {
      focusScreenHeading();
    };
  }, []);

  const onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onCancel();
      return;
    }
    if (event.key === 'Tab') {
      event.preventDefault();
      const next =
        document.activeElement === cancelRef.current ? confirmRef.current : cancelRef.current;
      next?.focus();
    }
  };

  return (
    <div class="dialog-overlay">
      <div
        class="dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={headingId}
        aria-describedby={detailId}
        onKeyDown={onKeyDown}
      >
        <h2 id={headingId}>{heading}</h2>
        <p id={detailId}>{detail}</p>
        <div class="dialog__actions">
          <button ref={cancelRef} type="button" onClick={onCancel}>
            {cancelLabel}
          </button>
          <button ref={confirmRef} type="button" onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
